import React, { useState, useEffect, useRef } from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { CreateSalesOrderRequest, SalesOrder } from '../../types/sales.types';
import { useActiveCustomers } from '../../hooks/useCustomers';
import { useInventory } from '../../hooks/useInventory';
import priceListService from '../../services/priceListService';
import { ProductPriceDetails } from '../../types/priceList';
import { Customer } from '../../types/customer.types';
import {
  Field,
  inputClass,
  inputErrorClass,
  primaryButtonClass,
  secondaryButtonClass,
} from '../ui/FormControls';
import { cn } from '../../lib/utils';
import { useActiveGstRates } from '../../hooks/useGstRates';
import { PlantField, plantPayload, usePlantChoice } from '../common/PlantField';

interface SalesOrderFormProps {
  onSubmit: (data: CreateSalesOrderRequest) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
  initialData?: SalesOrder;
  submitButtonText?: string;
  mode?: 'create' | 'edit';
  // Quotes may exceed current stock; orders may not
  checkStock?: boolean;
}

interface OrderItem {
  productId: string;
  quantity: number;
  unitPrice: number;
  discount: number; // amount in rupees, converted to a rate on submit
  // Set while the price and discount come from a price list
  priceListName?: string;
  listDiscountPercent?: number;
}

// A date in YYYY-MM-DD for date inputs, in the user's timezone
const toDateInput = (value?: string | Date) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

const newItem = (): OrderItem => ({ productId: '', quantity: 1, unitPrice: 0, discount: 0 });

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
  }).format(Number.isFinite(amount) ? amount : 0);
};

const PAYMENT_TERM_BY_DAYS: Record<number, string> = { 30: 'Net 30', 60: 'Net 60', 90: 'Net 90' };

const formatCustomerAddress = (customer: Customer) =>
  [customer.address, customer.city, [customer.state, customer.pincode].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');

// A numbered group of related fields
const Section: React.FC<{
  step: number;
  title: string;
  description: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}> = ({ step, title, description, action, children }) => (
  <section className="rounded-lg border border-gray-200 bg-white">
    <div className="flex items-center justify-between gap-3 rounded-t-lg border-b border-gray-200 bg-gray-50 px-4 py-3">
      <div className="flex items-center gap-3">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-xs font-semibold text-white">
          {step}
        </span>
        <div>
          <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
          <p className="mt-0.5 text-xs text-gray-500">{description}</p>
        </div>
      </div>
      {action}
    </div>
    {children}
  </section>
);

const sectionGrid = 'grid grid-cols-1 gap-x-4 gap-y-4 p-4 sm:grid-cols-2';

const SalesOrderForm: React.FC<SalesOrderFormProps> = ({
  onSubmit,
  onCancel,
  isLoading = false,
  initialData,
  submitButtonText = 'Create Sales Order',
  mode = 'create',
  checkStock = true,
}) => {
  const plant = usePlantChoice();
  const [plantAsked, setPlantAsked] = useState(false);
  const { customers } = useActiveCustomers();
  const { products } = useInventory();

  const [formData, setFormData] = useState({
    customerId: '',
    orderDate: toDateInput(new Date()),
    expectedDeliveryDate: '',
    notes: '',
    shippingAddress: '',

    // Delivery Terms
    deliveryTerms: '',
    paymentTerms: '',
    shippingMethod: '',
    deliveryLocation: '',
    specialInstructions: '',

    // Promised Dates Tracking
    promisedDate: '',
    confirmedDate: '',
    actualShipDate: '',
    actualDeliveryDate: '',
    deliveryDelayReason: '',
  });

  const [items, setItems] = useState<OrderItem[]>([newItem()]);
  // One GST rate for the whole order, from the GST rate master; null until one is picked
  const { optionsFor, defaultRate } = useActiveGstRates();
  const [pickedTaxRate, setPickedTaxRate] = useState<number | null>(null);
  const TAX_RATE = pickedTaxRate ?? defaultRate;
  const [errors, setErrors] = useState<Record<string, string>>({});
  // The address last filled in from a customer, so a typed address is never overwritten
  const autoFilledAddress = useRef('');

  // Populate form with initial data if provided
  useEffect(() => {
    if (initialData) {
      setFormData({
        customerId: initialData.customer?.id || initialData.customerId || '',
        orderDate: toDateInput(initialData.orderDate) || toDateInput(new Date()),
        expectedDeliveryDate: toDateInput(initialData.expectedDeliveryDate),
        notes: initialData.notes || '',
        shippingAddress: initialData.shippingAddress || '',
        deliveryTerms: initialData.deliveryTerms || '',
        paymentTerms: initialData.paymentTerms || '',
        shippingMethod: initialData.shippingMethod || '',
        deliveryLocation: initialData.deliveryLocation || '',
        specialInstructions: initialData.specialInstructions || '',
        promisedDate: toDateInput(initialData.promisedDate),
        confirmedDate: toDateInput(initialData.confirmedDate),
        actualShipDate: toDateInput(initialData.actualShipDate),
        actualDeliveryDate: toDateInput(initialData.actualDeliveryDate),
        deliveryDelayReason: initialData.deliveryDelayReason || '',
      });

      // Set items if they exist
      if (initialData.items && initialData.items.length > 0) {
        const existingRate = Number(initialData.items[0].taxRate);
        setPickedTaxRate(Number.isFinite(existingRate) ? existingRate : null);
        setItems(
          initialData.items.map((item) => ({
            productId: item.productId || '',
            quantity: Number(item.quantity) || 1,
            unitPrice: Number(item.unitPrice) || 0,
            discount: Number(item.discountAmount) || 0,
          })),
        );
      }
    }
  }, [initialData]);

  const lineSubtotal = (item: OrderItem) => item.quantity * item.unitPrice;
  const isPriced = (item: OrderItem) => !!item.productId && item.quantity > 0 && item.unitPrice > 0;

  const subtotal = items.reduce((sum, item) => (isPriced(item) ? sum + lineSubtotal(item) : sum), 0);
  const totalDiscount = items.reduce((sum, item) => (isPriced(item) ? sum + item.discount : sum), 0);
  const taxAmount = ((subtotal - totalDiscount) * TAX_RATE) / 100;
  const grandTotal = subtotal - totalDiscount + taxAmount;

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.customerId) newErrors.customerId = 'Customer is required';
    if (!formData.orderDate) newErrors.orderDate = 'Order date is required';
    if (!formData.expectedDeliveryDate) newErrors.expectedDeliveryDate = 'Expected delivery date is required';

    // Validate that delivery date is after order date
    if (formData.orderDate && formData.expectedDeliveryDate) {
      if (new Date(formData.expectedDeliveryDate) <= new Date(formData.orderDate)) {
        newErrors.expectedDeliveryDate = 'Delivery date must be after order date';
      }
    }

    // Validate items
    const validItems = items.filter((item) => item.productId && item.quantity > 0);
    if (validItems.length === 0) {
      newErrors.items = 'At least one item is required';
    }

    // Validate each item
    items.forEach((item, index) => {
      if (item.productId) {
        if (item.quantity <= 0 || isNaN(item.quantity)) {
          newErrors[`item_${index}_quantity`] = 'Quantity must be greater than 0';
        }
        if (item.unitPrice <= 0 || isNaN(item.unitPrice)) {
          newErrors[`item_${index}_unitPrice`] = 'Unit price must be greater than 0';
        }
        if (item.discount > lineSubtotal(item)) {
          newErrors[`item_${index}_discount`] = 'Discount cannot exceed the line amount';
        }

        // Check stock availability
        const product = products.find((p) => p.id === item.productId);
        if (checkStock && product && item.quantity > product.currentStock) {
          newErrors[`item_${index}_quantity`] = `Only ${product.currentStock} units available in stock`;
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPlantAsked(true);
    if (!validateForm() || (mode === 'create' && plant.missing)) {
      return;
    }

    const validItems = items.filter(isPriced);
    if (validItems.length === 0) {
      setErrors((prev) => ({
        ...prev,
        items: 'At least one valid item with positive quantity and price is required',
      }));
      return;
    }

    const orderData: CreateSalesOrderRequest = {
      customerId: formData.customerId,
      ...(mode === 'create' ? plantPayload(plant.plantId) : {}),
      orderDate: formData.orderDate,
      expectedDeliveryDate: formData.expectedDeliveryDate,
      items: validItems.map((item) => {
        const itemSubtotal = lineSubtotal(item);
        const discountRate = itemSubtotal > 0 ? (item.discount * 100) / itemSubtotal : 0;
        return {
          productId: item.productId,
          quantity: Math.max(1, Math.floor(item.quantity)), // Ensure positive integer
          unitPrice: Math.max(0.01, Number(Number(item.unitPrice).toFixed(2))), // Ensure positive price
          // Keep enough precision that the rate turns back into the exact rupee discount
          discountRate: Math.min(100, Math.max(0, Math.round(discountRate * 1e6) / 1e6)),
          taxRate: TAX_RATE,
          // The form already shows the price-list price, so the server keeps exactly what was approved here
          usePriceList: false,
        };
      }),
      notes: formData.notes || undefined,
      shippingAddress: formData.shippingAddress || undefined,

      // Delivery Terms
      deliveryTerms: formData.deliveryTerms || undefined,
      paymentTerms: formData.paymentTerms || undefined,
      shippingMethod: formData.shippingMethod || undefined,
      deliveryLocation: formData.deliveryLocation || undefined,
      specialInstructions: formData.specialInstructions || undefined,

      // Promised Dates Tracking
      promisedDate: formData.promisedDate || undefined,
      confirmedDate: formData.confirmedDate || undefined,
      actualShipDate: formData.actualShipDate || undefined,
      actualDeliveryDate: formData.actualDeliveryDate || undefined,
      deliveryDelayReason: formData.deliveryDelayReason || undefined,
    };

    try {
      await onSubmit(orderData);
    } catch (error) {
      // The caller reports the failure; keep the form open so nothing is lost
    }
  };

  const handleFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;

    if (name === 'customerId') {
      const customer = customers.find((c) => c.id === value);
      const address = customer ? formatCustomerAddress(customer) : '';
      setFormData((prev) => ({
        ...prev,
        customerId: value,
        shippingAddress:
          !prev.shippingAddress || prev.shippingAddress === autoFilledAddress.current
            ? address
            : prev.shippingAddress,
        paymentTerms:
          prev.paymentTerms || (customer ? PAYMENT_TERM_BY_DAYS[Number(customer.paymentTerms)] || '' : ''),
      }));
      autoFilledAddress.current = address;
      // A different customer can have their own price list
      items.forEach((item, index) => applyPriceList(index, item.productId, item.quantity, value));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const round2 = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;

  // Looks up the price list price for one line and fills it in. The result is
  // dropped if the line changed while the request was running.
  const applyPriceList = async (index: number, productId: string, quantity: number, customerId: string) => {
    if (!productId || quantity <= 0) return;

    let details: ProductPriceDetails | null = null;
    try {
      details = await priceListService.getProductPriceDetails({
        productId,
        customerId: customerId || undefined,
        quantity,
      });
    } catch (error) {
      return; // Keep the price already shown
    }

    setItems((current) =>
      current.map((item, i) => {
        if (i !== index || item.productId !== productId || item.quantity !== quantity) return item;

        if (!details) {
          // No list covers this line any more: fall back to the product's own price
          if (!item.priceListName) return item;
          const product = products.find((p) => p.id === productId);
          return {
            ...item,
            unitPrice: product ? Number(product.price) || 0 : item.unitPrice,
            discount: 0,
            priceListName: undefined,
            listDiscountPercent: undefined,
          };
        }

        return {
          ...item,
          unitPrice: Number(details.finalPrice),
          discount: round2((quantity * Number(details.finalPrice) * Number(details.discountPercent)) / 100),
          priceListName: details.priceListName,
          listDiscountPercent: Number(details.discountPercent),
        };
      }),
    );
  };

  const handleItemChange = (index: number, field: keyof OrderItem, value: string | number) => {
    const newItems = [...items];

    if (field === 'productId') {
      const product = products.find((p) => p.id === value);
      newItems[index] = {
        ...newItems[index],
        productId: value as string,
        // Auto-populate unit price when product is selected
        unitPrice: product ? Number(product.price) || 0 : newItems[index].unitPrice,
        discount: 0,
        priceListName: undefined,
        listDiscountPercent: undefined,
      };
    } else {
      const numValue = Number(value);
      if (isNaN(numValue) || numValue < 0) return; // Don't allow invalid or negative values
      const current = newItems[index];

      if (field === 'quantity') {
        const quantity = Math.floor(numValue);
        newItems[index] = {
          ...current,
          quantity,
          // A price-list discount is a percentage, so it follows the quantity
          discount:
            current.listDiscountPercent !== undefined
              ? round2((quantity * current.unitPrice * current.listDiscountPercent) / 100)
              : current.discount,
        };
      } else {
        // Typing a price or discount by hand takes the line off the price list
        newItems[index] = {
          ...current,
          [field]: numValue,
          priceListName: undefined,
          listDiscountPercent: undefined,
        };
      }
    }

    setItems(newItems);

    // A new product or quantity may have a different price-list price (quantity tiers)
    if (field === 'productId' || field === 'quantity') {
      applyPriceList(index, newItems[index].productId, newItems[index].quantity, formData.customerId);
    }

    // Clear errors for this item
    const errorKey = `item_${index}_${field}`;
    if (errors[errorKey] || errors.items) {
      setErrors((prev) => ({ ...prev, [errorKey]: '', items: '' }));
    }
  };

  const addItem = () => {
    setItems([...items, newItem()]);
  };

  const removeItem = (index: number) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const fieldClass = (key: string) => cn(inputClass, errors[key] && inputErrorClass);

  const selectedCustomer = customers.find((c) => c.id === formData.customerId);
  const pricedItems = items.filter(isPriced);
  const totalUnits = pricedItems.reduce((sum, item) => sum + item.quantity, 0);
  const errorCount = Object.values(errors).filter(Boolean).length;

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
        {/* Left: the order itself, in the order it is filled in */}
        <div className="space-y-5 xl:col-span-2">
          <Section step={1} title="Customer and dates" description="Who is buying, and when it is due.">
            <div className={sectionGrid}>
              <Field
                label="Customer"
                htmlFor="customerId"
                required
                error={errors.customerId}
                className="sm:col-span-2"
              >
                <select
                  name="customerId"
                  id="customerId"
                  value={formData.customerId}
                  onChange={handleFormChange}
                  className={fieldClass('customerId')}
                >
                  <option value="">Select a customer</option>
                  {customers.map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name} ({customer.code})
                    </option>
                  ))}
                </select>
              </Field>

              {selectedCustomer && (
                <dl className="grid grid-cols-2 gap-x-4 gap-y-2 rounded-md bg-indigo-50 px-3 py-2.5 text-sm sm:col-span-2 sm:grid-cols-4">
                  <div>
                    <dt className="text-xs text-gray-500">Contact</dt>
                    <dd className="truncate font-medium text-gray-900">{selectedCustomer.contactPerson || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500">Phone</dt>
                    <dd className="truncate font-medium text-gray-900">
                      {selectedCustomer.contactPhone || selectedCustomer.phone || '—'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500">Credit limit</dt>
                    <dd className="font-medium text-gray-900 tabular-nums">
                      {formatCurrency(Number(selectedCustomer.creditLimit) || 0)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500">Payment terms</dt>
                    <dd className="font-medium text-gray-900">
                      {selectedCustomer.paymentTerms ? `${selectedCustomer.paymentTerms} days` : '—'}
                    </dd>
                  </div>
                </dl>
              )}

              <Field label="Order Date" htmlFor="orderDate" required error={errors.orderDate}>
                <input
                  type="date"
                  name="orderDate"
                  id="orderDate"
                  value={formData.orderDate}
                  onChange={handleFormChange}
                  className={fieldClass('orderDate')}
                />
              </Field>

              <Field
                label="Expected Delivery Date"
                htmlFor="expectedDeliveryDate"
                required
                error={errors.expectedDeliveryDate}
              >
                <input
                  type="date"
                  name="expectedDeliveryDate"
                  id="expectedDeliveryDate"
                  value={formData.expectedDeliveryDate}
                  onChange={handleFormChange}
                  min={formData.orderDate}
                  className={fieldClass('expectedDeliveryDate')}
                />
              </Field>
            </div>
          </Section>

          <Section
            step={2}
            title="Order items"
            description={`Products, quantities and prices. GST is ${TAX_RATE}%; change it in the summary.`}
            action={
              <button
                type="button"
                onClick={addItem}
                className="inline-flex shrink-0 items-center rounded-md border border-indigo-200 bg-white px-3 py-1.5 text-sm font-semibold text-indigo-700 hover:bg-indigo-50"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Add Item
              </button>
            }
          >
            <div className="space-y-3 p-4">
              {errors.items && <p className="text-sm text-red-600">{errors.items}</p>}

              {items.map((item, index) => {
                const product = products.find((p) => p.id === item.productId);
                return (
                  <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Item {index + 1}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        disabled={items.length === 1}
                        className="rounded p-1 text-red-600 hover:bg-red-50 hover:text-red-900 disabled:text-gray-300 disabled:hover:bg-transparent"
                        title="Remove item"
                        aria-label="Remove item"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-12">
                      <Field
                        label="Product"
                        htmlFor={`item-${index}-product`}
                        required
                        className="col-span-2 sm:col-span-5"
                        hint={
                          product
                            ? `Available: ${product.currentStock} ${product.unit}${
                                !checkStock && item.quantity > product.currentStock ? ' — more than is in stock' : ''
                              }${item.priceListName ? ` · Price list: ${item.priceListName}` : ''}`
                            : undefined
                        }
                      >
                        <select
                          id={`item-${index}-product`}
                          value={item.productId}
                          onChange={(e) => handleItemChange(index, 'productId', e.target.value)}
                          className={inputClass}
                        >
                          <option value="">Select product</option>
                          {products.map((option) => (
                            <option key={option.id} value={option.id}>
                              {option.name} (Stock: {option.currentStock})
                            </option>
                          ))}
                        </select>
                      </Field>

                      <Field
                        label="Quantity"
                        htmlFor={`item-${index}-quantity`}
                        required
                        className="sm:col-span-2"
                        error={errors[`item_${index}_quantity`]}
                      >
                        <input
                          type="number"
                          id={`item-${index}-quantity`}
                          min="0"
                          step="0.0001"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                          className={fieldClass(`item_${index}_quantity`)}
                        />
                      </Field>

                      <Field
                        label="Unit Price (₹)"
                        htmlFor={`item-${index}-price`}
                        required
                        className="sm:col-span-2"
                        error={errors[`item_${index}_unitPrice`]}
                      >
                        <input
                          type="number"
                          id={`item-${index}-price`}
                          min="0.01"
                          step="0.01"
                          value={item.unitPrice}
                          onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value)}
                          className={fieldClass(`item_${index}_unitPrice`)}
                        />
                      </Field>

                      <Field
                        label="Discount (₹)"
                        htmlFor={`item-${index}-discount`}
                        className="sm:col-span-3"
                        error={errors[`item_${index}_discount`]}
                      >
                        <input
                          type="number"
                          id={`item-${index}-discount`}
                          min="0"
                          step="0.01"
                          value={item.discount}
                          onChange={(e) => handleItemChange(index, 'discount', e.target.value)}
                          className={fieldClass(`item_${index}_discount`)}
                        />
                      </Field>
                    </div>

                    <p className="mt-3 flex justify-end gap-2 border-t border-gray-200 pt-2 text-sm text-gray-600">
                      Line total (before tax)
                      <span className="font-semibold text-gray-900 tabular-nums">
                        {formatCurrency(lineSubtotal(item) - item.discount)}
                      </span>
                    </p>
                  </div>
                );
              })}
            </div>
          </Section>

          <Section step={3} title="Shipping and payment" description="Where it goes, how it ships and how it is paid.">
            <div className={sectionGrid}>
              <Field
                label="Shipping Address"
                htmlFor="shippingAddress"
                className="sm:col-span-2"
                hint="Filled in from the customer; change it to ship somewhere else."
              >
                <textarea
                  name="shippingAddress"
                  id="shippingAddress"
                  rows={2}
                  value={formData.shippingAddress}
                  onChange={handleFormChange}
                  placeholder="Select a customer to fill in their address"
                  className={inputClass}
                />
              </Field>

              <Field label="Shipping Method" htmlFor="shippingMethod">
                <select
                  name="shippingMethod"
                  id="shippingMethod"
                  value={formData.shippingMethod}
                  onChange={handleFormChange}
                  className={inputClass}
                >
                  <option value="">Select Shipping Method</option>
                  <option value="Air">Air Freight</option>
                  <option value="Sea">Sea Freight</option>
                  <option value="Road">Road Transport</option>
                  <option value="Rail">Rail Transport</option>
                  <option value="Express">Express Delivery</option>
                  <option value="Standard">Standard Shipping</option>
                  <option value="Pickup">Customer Pickup</option>
                  <option value="Other">Other</option>
                </select>
              </Field>

              <Field label="Delivery Location" htmlFor="deliveryLocation">
                <input
                  type="text"
                  name="deliveryLocation"
                  id="deliveryLocation"
                  value={formData.deliveryLocation}
                  onChange={handleFormChange}
                  placeholder="Specific delivery location"
                  className={inputClass}
                />
              </Field>

              <Field label="Delivery Terms" htmlFor="deliveryTerms">
                <select
                  name="deliveryTerms"
                  id="deliveryTerms"
                  value={formData.deliveryTerms}
                  onChange={handleFormChange}
                  className={inputClass}
                >
                  <option value="">Select Delivery Terms</option>
                  <option value="FOB">FOB (Free on Board)</option>
                  <option value="CIF">CIF (Cost, Insurance and Freight)</option>
                  <option value="DDP">DDP (Delivered Duty Paid)</option>
                  <option value="EXW">EXW (Ex Works)</option>
                  <option value="FCA">FCA (Free Carrier)</option>
                  <option value="DAP">DAP (Delivered at Place)</option>
                  <option value="Other">Other</option>
                </select>
              </Field>

              <Field label="Payment Terms" htmlFor="paymentTerms">
                <select
                  name="paymentTerms"
                  id="paymentTerms"
                  value={formData.paymentTerms}
                  onChange={handleFormChange}
                  className={inputClass}
                >
                  <option value="">Select Payment Terms</option>
                  <option value="Net 30">Net 30 days</option>
                  <option value="Net 60">Net 60 days</option>
                  <option value="Net 90">Net 90 days</option>
                  <option value="Advance">100% Advance</option>
                  <option value="50% Advance">50% Advance</option>
                  <option value="COD">Cash on Delivery</option>
                  <option value="Due on Receipt">Due on Receipt</option>
                  <option value="Other">Other</option>
                </select>
              </Field>

              <Field label="Special Instructions" htmlFor="specialInstructions" className="sm:col-span-2">
                <textarea
                  name="specialInstructions"
                  id="specialInstructions"
                  rows={2}
                  value={formData.specialInstructions}
                  onChange={handleFormChange}
                  placeholder="Any special delivery instructions..."
                  className={inputClass}
                />
              </Field>
            </div>
          </Section>

          <Section
            step={4}
            title="Delivery commitments"
            description={
              mode === 'edit'
                ? 'Dates promised to the customer, and what actually happened.'
                : 'Optional. Dates promised to the customer.'
            }
          >
            <div className={sectionGrid}>
              <Field label="Promised Date" htmlFor="promisedDate" hint="The date given to the customer.">
                <input
                  type="date"
                  name="promisedDate"
                  id="promisedDate"
                  value={formData.promisedDate}
                  onChange={handleFormChange}
                  className={inputClass}
                />
              </Field>

              <Field label="Confirmed Date" htmlFor="confirmedDate" hint="The date confirmed by production.">
                <input
                  type="date"
                  name="confirmedDate"
                  id="confirmedDate"
                  value={formData.confirmedDate}
                  onChange={handleFormChange}
                  className={inputClass}
                />
              </Field>

              {/* Actual dates only make sense once the order exists */}
              {mode === 'edit' && (
                <>
                  <Field label="Actual Ship Date" htmlFor="actualShipDate">
                    <input
                      type="date"
                      name="actualShipDate"
                      id="actualShipDate"
                      value={formData.actualShipDate}
                      onChange={handleFormChange}
                      className={inputClass}
                    />
                  </Field>

                  <Field label="Actual Delivery Date" htmlFor="actualDeliveryDate">
                    <input
                      type="date"
                      name="actualDeliveryDate"
                      id="actualDeliveryDate"
                      value={formData.actualDeliveryDate}
                      onChange={handleFormChange}
                      className={inputClass}
                    />
                  </Field>

                  <Field label="Delivery Delay Reason" htmlFor="deliveryDelayReason" className="sm:col-span-2">
                    <textarea
                      name="deliveryDelayReason"
                      id="deliveryDelayReason"
                      rows={2}
                      value={formData.deliveryDelayReason}
                      onChange={handleFormChange}
                      placeholder="Reason for any delivery delays..."
                      className={inputClass}
                    />
                  </Field>
                </>
              )}
            </div>
          </Section>
        </div>

        {/* Right: running summary and notes */}
        <aside className="xl:col-span-1">
          <div className="space-y-5 xl:sticky xl:top-4">
            <section className="rounded-lg border border-gray-200 bg-white">
              <h4 className="rounded-t-lg border-b border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-900">
                Order summary
              </h4>
              <div className="space-y-4 p-4 text-sm">
                <div>
                  <p className="text-xs text-gray-500">Customer</p>
                  <p className="font-medium text-gray-900">
                    {selectedCustomer ? selectedCustomer.name : 'Not selected'}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-md bg-gray-50 px-3 py-2">
                    <p className="text-xs text-gray-500">Items</p>
                    <p className="text-lg font-semibold text-gray-900 tabular-nums">{pricedItems.length}</p>
                  </div>
                  <div className="rounded-md bg-gray-50 px-3 py-2">
                    <p className="text-xs text-gray-500">Units</p>
                    <p className="text-lg font-semibold text-gray-900 tabular-nums">{totalUnits}</p>
                  </div>
                </div>
                <dl>
                  <div className="flex justify-between py-1">
                    <dt className="text-gray-600">Subtotal</dt>
                    <dd className="font-medium text-gray-900 tabular-nums">{formatCurrency(subtotal)}</dd>
                  </div>
                  <div className="flex justify-between py-1">
                    <dt className="text-gray-600">Discount</dt>
                    <dd className="font-medium text-gray-900 tabular-nums">-{formatCurrency(totalDiscount)}</dd>
                  </div>
                  <div className="flex justify-between py-1">
                    <dt className="flex items-center gap-2 text-gray-600">
                      <label htmlFor="order-gst-rate">GST</label>
                      <select
                        id="order-gst-rate"
                        value={TAX_RATE}
                        onChange={(event) => setPickedTaxRate(Number(event.target.value))}
                        className="rounded-md border border-gray-300 bg-white py-1 pl-2 pr-7 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      >
                        {optionsFor(TAX_RATE).map((rate) => (
                          <option key={rate} value={rate}>
                            {rate}%
                          </option>
                        ))}
                      </select>
                    </dt>
                    <dd className="font-medium text-gray-900 tabular-nums">{formatCurrency(taxAmount)}</dd>
                  </div>
                  <div className="mt-2 flex justify-between border-t border-gray-200 pt-3">
                    <dt className="font-semibold text-gray-900">Grand Total</dt>
                    <dd className="text-lg font-semibold text-gray-900 tabular-nums">
                      {formatCurrency(grandTotal)}
                    </dd>
                  </div>
                </dl>
              </div>
            </section>

            {mode === 'create' && plant.scope.required && (
              <section className="rounded-lg border border-gray-200 bg-white p-4">
                <PlantField id="so-plant" scope={plant.scope} value={plant.plantId} onChange={plant.setPlantId} showError={plantAsked} />
              </section>
            )}

            <section className="rounded-lg border border-gray-200 bg-white">
              <h4 className="rounded-t-lg border-b border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-900">
                Notes
              </h4>
              <div className="p-4">
                <label htmlFor="notes" className="sr-only">
                  Notes
                </label>
                <textarea
                  name="notes"
                  id="notes"
                  rows={4}
                  value={formData.notes}
                  onChange={handleFormChange}
                  placeholder="Any additional notes or instructions"
                  className={inputClass}
                />
              </div>
            </section>
          </div>
        </aside>
      </div>

      {/* Footer keeps the total and any problems in view next to the buttons */}
      <div className="-mx-6 -mb-4 mt-6 flex flex-col gap-3 rounded-b-lg border-t border-gray-200 bg-gray-50 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-sm">
          <span className="text-gray-600">Grand Total </span>
          <span className="text-base font-semibold text-gray-900 tabular-nums">{formatCurrency(grandTotal)}</span>
          {errorCount > 0 && (
            <span className="ml-3 font-medium text-red-600">
              {errorCount} {errorCount === 1 ? 'field needs' : 'fields need'} attention
            </span>
          )}
        </div>
        <div className="flex flex-col-reverse gap-3 sm:flex-row">
          <button type="button" onClick={onCancel} className={secondaryButtonClass}>
            Cancel
          </button>
          <button type="submit" disabled={isLoading} className={primaryButtonClass}>
            {isLoading ? (mode === 'create' ? 'Creating...' : 'Updating...') : submitButtonText}
          </button>
        </div>
      </div>
    </form>
  );
};

export default SalesOrderForm;
